import 'swiper/css';

/* eslint-disable max-lines */
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Navigation } from 'lucide-react';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Error, Spinner } from 'src/components';
import { useGetPois, useQueryHelper } from 'src/queries';
import { useOrganizationState } from 'src/state';
import { PoiFirestoreResponse } from 'src/types';
import { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import AtmPin from '../../assets/icons/atm-pin.png';
import BeautyPin from '../../assets/icons/beauty-pin.png';
import CulturePin from '../../assets/icons/culture-pin.png';
import ExchangePin from '../../assets/icons/exchange-pin.png';
import FloristPin from '../../assets/icons/florist-pin.png';
import ParkingPin from '../../assets/icons/parking-pin.png';
import PharmacyPin from '../../assets/icons/pharmacy-pin.png';
import StationPin from '../../assets/icons/station-pin.png';
import StorePin from '../../assets/icons/store-pin.png';
import {
  Place,
  PlaceAddress,
  PlaceButton,
  PlaceContent,
  PlaceIcon,
  PlaceName,
  PlaceType,
  PlacetypeContainer,
  TypeLabel,
  Types,
} from './Nearby.styled';

const containerStyle = {
  width: '100%',
  height: 'calc(100dvh - 35px)',
};

export const Nearby: FC = () => {
  const { t } = useTranslation();
  const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
  const [activeType, setActiveType] = useState('all');
  const [activePoi, setActivePoi] = useState<any>(null);
  const { organizationData } = useOrganizationState();
  const poisCollections = useGetPois(organizationData.hotel);
  const { data: pois } = poisCollections;
  const { isLoading, isError } = useQueryHelper({ queries: [poisCollections] });
  const mapRef = useRef<google.maps.Map | null>(null);
  const swiperRef = useRef<SwiperType | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: API_KEY || '',
  });

  const center = useMemo(() => {
    if (pois && pois.length > 0 && pois[0].location) {
      return {
        lat: pois[0].location.latitude,
        lng: pois[0].location.longitude,
      };
    }
    return {
      lat: -3.745,
      lng: -38.523,
    };
  }, [pois]);

  const labels = pois && pois.length ? [...new Set(pois.map((poi: PoiFirestoreResponse) => poi.placeType))] : [];

  const filteredPois = useMemo(() => {
    if (!pois) return [];
    if (activeType === 'all') return pois;
    return pois.filter((poi: PoiFirestoreResponse) => poi.placeType === activeType);
  }, [pois, activeType]);

  const onLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    fitBoundsToMarkers();
  }, []);

  function debounce<T extends(...args: any[]) => any>(func: T, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  const fitBoundsToMarkers = useCallback(() => {
    if (!mapRef.current) return;

    if (activePoi && activePoi.location) {
      const position = {
        lat: activePoi.location.latitude,
        lng: activePoi.location.longitude,
      };
      setTimeout(() => {
        mapRef.current?.panTo(position);
        const zoom = mapRef.current?.getZoom();
        if (mapRef.current && zoom && zoom !== 15) {
          mapRef.current.setZoom(15);
        }
      }, 100);

      return;
    }

    if (filteredPois.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();

      filteredPois.forEach((poi: PoiFirestoreResponse) => {
        if (poi.location) {
          bounds.extend({
            lat: poi.location.latitude,
            lng: poi.location.longitude,
          });
        }
      });

      mapRef.current.fitBounds(bounds);

      if (filteredPois.length === 1) {
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.setZoom(15);
          }
        }, 2000);
      }
    }
  }, [filteredPois, activePoi]);

  const debouncedFitBounds = useMemo(() => debounce(fitBoundsToMarkers, 300), [fitBoundsToMarkers]);

  useEffect(() => {
    if (isLoaded) {
      debouncedFitBounds();
    }
  }, [debouncedFitBounds, isLoaded, activeType]);
  const handleTypeChange = (type: string) => {
    setActiveType(type);
    setActivePoi(null);
  };
  const handleMarkerClick = (poi: any) => {
    setActivePoi(poi);
  };
  const handleSlideChange = (swiper: SwiperType) => {
    const currentIndex = swiper.activeIndex;
    if (filteredPois[currentIndex]) {
      handleMarkerClick(filteredPois[currentIndex]);
    }
  };
  useEffect(() => {
    if (
      activePoi
      && swiperRef.current
      && filteredPois.length
      && swiperRef.current.activeIndex !== undefined
    ) {
      const poiIndex = filteredPois.findIndex((poi: PoiFirestoreResponse) => poi.id === activePoi.id);
      if (poiIndex !== -1 && poiIndex !== swiperRef.current.activeIndex) {
        try {
          swiperRef.current.slideTo(poiIndex);
        } catch (err) {
          console.warn('Błąd przy slideTo:', err);
        }
      }
    }
  }, [activePoi, filteredPois]);

  const temporaryNames = [
    { id: 'store', name: t('map_screen.shops'), icon: StorePin },
    { id: 'beauty_salon', name: t('map_screen.beauty'), icon: BeautyPin },
    { id: 'art_gallery', name: t('map_screen.culture'), icon: CulturePin },
    { id: 'parking', name: t('map_screen.parking'), icon: ParkingPin },
    { id: 'atm', name: t('map_screen.atm'), icon: AtmPin },
    { id: 'gas_station', name: t('map_screen.petrol_station'), icon: StationPin },
    { id: 'finance', name: t('map_screen.exchange'), icon: ExchangePin },
    { id: 'pharmacy', name: t('map_screen.pharmacies'), icon: PharmacyPin },
    { id: 'florist', name: t('map_screen.florist'), icon: FloristPin },
  ];

  if (isLoading) {
    return (
      <Spinner global />
    );
  }

  if (isError) {
    return <Error errorMessage="No pois found" />;
  }

  return (
    <>
      <Helmet>
        <title>Hotelspot | W pobliżu</title>
        <meta name="description" content="Hotelspot - W pobliżu" />
      </Helmet>
      {isLoaded ? (

        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={10}
          onLoad={onLoad}
          options={{ disableDefaultUI: true }}
          onClick={() => setActivePoi(null)}

        >
          <Types>
            <TypeLabel
              active={activeType === 'all'}
              onClick={() => handleTypeChange('all')}
            >
              {t('map_screen.all')}
            </TypeLabel>
            {labels.map((label: unknown) => {
              const labelStr = label as string;
              return (
                <TypeLabel
                  key={labelStr}
                  active={activeType === labelStr}
                  onClick={() => handleTypeChange(labelStr)}
                >
                  {temporaryNames.find((name) => name.id === labelStr)?.name || labelStr}
                </TypeLabel>
              );
            })}
          </Types>
          {filteredPois.map((poi: PoiFirestoreResponse) => (
            <Marker
              key={poi.id}
              position={{
                lat: poi.location.latitude,
                lng: poi.location.longitude,
              }}
              title={poi.placeType}
              onClick={() => handleMarkerClick(poi)}
              icon={temporaryNames.find((name) => name.id === poi.placeType)?.icon}
            />
          ))}
          <Swiper
            ref={swiperRef}
            style={{ position: 'absolute', height: '250px', bottom: '30px', left: 0, right: 0, padding: '20px' }}
            spaceBetween={18}
            slidesPerView={1.1}
            lazy="true"
            breakpoints={{
              768: { slidesPerView: 2.2 },
              1024: { slidesPerView: 3.2 },
            }}
            onSlideChange={() => {
              if (swiperRef.current) {
                handleSlideChange(swiperRef.current);
              }
            }}
            onSwiper={(swiper: SwiperType) => { swiperRef.current = swiper; }}
          >
            {filteredPois.map((poi:PoiFirestoreResponse) => (
              <SwiperSlide
                style={{ display: 'flex', alignItems: 'flex-end' }}
                onClick={() => handleMarkerClick(poi)}
                key={poi.id}
              >
                <Place>
                  <PlaceContent>
                    <PlacetypeContainer>
                      <PlaceIcon src={temporaryNames.find((name) => name.id === poi.placeType)?.icon} />
                      <PlaceType>
                        {temporaryNames.find((name) => name.id === poi.placeType)?.name || poi.placeType}
                      </PlaceType>
                      <PlaceButton
                        href={`https://www.google.com/maps/search/?api=1&query=${poi.location.latitude},${poi.location.longitude}`}
                      >
                        <Navigation color="#1C1C1C" strokeWidth={1.5} size={24} />
                      </PlaceButton>
                    </PlacetypeContainer>
                    <PlaceName>{poi.name}</PlaceName>
                    <PlaceAddress>{poi.address}</PlaceAddress>
                  </PlaceContent>
                </Place>
              </SwiperSlide>
            ))}
          </Swiper>
        </GoogleMap>

      ) : <Spinner global />}
    </>
  );
};
